/* One assignment drives shipping copy and the existing Rebuy shipping tier.
 * Display only: Shopify/Intelligems remains responsible for actual entitlement.
 */
(function (root) {
  'use strict';
  const api = {
    resolve(config, market, user) {
      const experiment = config.markets?.[market.country];
      if (!experiment || market.currency !== experiment.currency || !user) return null;
      const experiments = user.getExperiments?.();
      if (!Array.isArray(experiments)) return null;
      const scoped = experiments.find(item => item?.id === experiment.id);
      if (!scoped || (!config.liveEnabled && !scoped.isPreview)) return null;
      const group = user.getTestGroup?.(experiment.id);
      const threshold = experiment.groups?.[group?.id];
      if (!Number.isInteger(threshold) || threshold <= 0) return null;
      return { country: market.country, currency: market.currency, threshold,
        experimentId: experiment.id, groupId: group.id, isPreview: !!scoped.isPreview };
    },
    edits(text, country, threshold) {
      // Complete shipping phrases only. Never replace bare dollar amounts.
      const baseline = country === 'AU' ? 99 : country === 'NZ' ? 79 : null;
      if (!baseline) return [];
      const prefix = country === 'AU' ? 'AU' : 'NZ';
      const phrases = [
        [`orders of ${prefix}$${baseline} or more`, `orders of ${prefix}$${threshold} or more`],
        [`FREE ${prefix} shipping on orders $${baseline}+`, `FREE ${prefix} shipping on orders $${threshold}+`],
        [`orders ${prefix}$${baseline}+`, `orders ${prefix}$${threshold}+`],
        [`Spend $${baseline} for free shipping`, `Spend $${threshold} for free shipping`],
        [`Free shipping over $${baseline}`, `Free shipping on $${threshold} or more`],
        [`free shipping over $${baseline}`, `free shipping on $${threshold} or more`],
      ];
      const edits = [];
      for (const [before, after] of phrases) {
        const escaped = before.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const boundary = /\d$/.test(before) ? '(?!\\d|\\.\\d)' : '';
        const pattern = new RegExp(escaped + boundary, 'g');
        for (const match of text.matchAll(pattern)) {
          let left = 0, right = 0;
          while (left < before.length && left < after.length && before[left] === after[left]) left++;
          while (right < before.length - left && right < after.length - left &&
                 before[before.length - 1 - right] === after[after.length - 1 - right]) right++;
          if (left < before.length || left < after.length) {
            edits.push({start: match.index + left, end: match.index + before.length - right,
              value: after.slice(left, after.length - right)});
          }
        }
      }
      return edits.sort((a, b) => b.start - a.start);
    },
    replaceParts(parts, country, threshold) {
      const output = [...parts];
      const offsets = []; let offset = 0;
      for (const part of parts) { offsets.push(offset); offset += part.length; }
      for (const edit of api.edits(parts.join(''), country, threshold)) {
        let inserted = false;
        for (let i = 0; i < parts.length; i++) {
          const start = offsets[i], end = start + parts[i].length;
          if (end <= edit.start || start >= edit.end) continue;
          const left = Math.max(0, edit.start - start), right = Math.min(parts[i].length, edit.end - start);
          output[i] = output[i].slice(0, left) + (inserted ? '' : edit.value) + output[i].slice(right);
          inserted = true;
        }
      }
      return output;
    },
    replace(text, country, threshold) {
      return api.replaceParts([text], country, threshold)[0];
    },
    subscriptionKind(cart) {
      if (!Array.isArray(cart?.items) || cart.items.length === 0) return 'empty';
      const paidItems = cart.items.filter(item => Number(item.final_line_price ?? item.line_price) > 0);
      if (!paidItems.length) return 'empty';
      const count = paidItems.filter(item => item.selling_plan_allocation || item.selling_plan).length;
      return count === 0 ? 'one-time' : count === paidItems.length ? 'subscription' : 'mixed';
    },
    tierField(tier, state) {
      if (!tier || tier.type !== 'shipping') return null;
      const currencies = tier.presentment_currencies;
      if (currencies?.enabled) {
        // Installed Rebuy getTierMinimum prioritises presentment cents over minimum.
        const currency = state?.currency;
        if (!['AUD', 'NZD'].includes(currency)) return null;
        const keys = [currency, currency.toLowerCase()].filter(key =>
          Object.prototype.hasOwnProperty.call(currencies, key));
        if (keys.length !== 1 || !Number.isFinite(currencies[keys[0]])) return null;
        return {object: currencies, key: keys[0], unit: 100};
      }
      return Number.isFinite(tier.minimum) ? {object: tier, key: 'minimum', unit: null} : null;
    },
    updateTier(tier, state, baseline) {
      const field = api.tierField(tier, state);
      if (!field || !Number.isInteger(state?.threshold) || state.threshold <= 0) return false;
      const original = field.object[field.key];
      const unit = field.unit || (original === baseline ? 1 : original === baseline * 100 ? 100 : null);
      if (!unit || original !== baseline * unit) return false;
      try {
        field.object[field.key] = state.threshold * unit;
        return field.object[field.key] === state.threshold * unit;
      } catch (_) { return false; }
    }

  };
  root.MoodiShippingThreshold = api;
  if (!root.document) return;
  const configNode = document.getElementById('moodi-shipping-test-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const originals = new WeakMap();
  const tiers = new Map();
  let queued = false;
  function refresh() {
    queued = false;
    const market = config.market;
    let state = null;
    try { state = api.resolve(config, market, root.igData?.user); } catch (_) { /* Baseline on SDK error. */ }
    document.querySelectorAll('[data-moodi-shipping-copy], .shopify-policy__body').forEach(container => {
      // Keep links, icons, country labels and formatting intact.
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const groups = new Map();
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement?.closest('script, style, template, textarea')) continue;
        if (!originals.has(node)) originals.set(node, {original: node.nodeValue, applied: node.nodeValue});
        const record = originals.get(node);
        if (node.nodeValue !== record.applied) record.original = node.nodeValue;
        // Join inline formatting only within one text block. Never match a
        // shipping phrase across paragraphs, list items or table cells.
        const block = node.parentElement?.closest('p, li, td, th, summary, h1, h2, h3, h4, div') || container;
        if (!groups.has(block)) groups.set(block, []);
        groups.get(block).push(node);
      }
      for (const nodes of groups.values()) {
        const parts = nodes.map(node => originals.get(node).original);
        const output = state ? api.replaceParts(parts, state.country, state.threshold) : parts;
        nodes.forEach((node, i) => {
          if (node.nodeValue !== output[i]) node.nodeValue = output[i];
          originals.get(node).applied = output[i];
        });
      }
    });
    const smartCart = root.Rebuy?.SmartCart;
    const currentTiers = smartCart?.progressBar?.tiers;
    let rebuyStatus = 'waiting';
    let activeField = null;
    let activeTier = null;
    if (Array.isArray(currentTiers)) {
      const shippingTiers = currentTiers.filter(tier => tier?.type === 'shipping');
      if (shippingTiers.length === 1 && state) {
        activeTier = shippingTiers[0];
        activeField = api.tierField(activeTier, state);
        rebuyStatus = activeField ? 'updated' : 'unsupported-schema';
      } else rebuyStatus = shippingTiers.length === 1 ? 'baseline' : 'unsupported-schema';
    }
    // Restore only fields we own, without overwriting a later app-side change.
    for (const [object, fields] of tiers) {
      for (const [key, record] of fields) {
        if (activeField?.object === object && activeField.key === key) continue;
        try {
          if (object[key] === record.applied && object[key] !== record.original) object[key] = record.original;
        } catch (_) { rebuyStatus = 'unsupported-schema'; }
        if (object[key] === record.applied && record.applied !== record.original) rebuyStatus = 'unsupported-schema';
        else fields.delete(key);
      }
      if (!fields.size) tiers.delete(object);
    }
    if (activeField) {
      const {object, key} = activeField;
      if (!tiers.has(object)) tiers.set(object, new Map());
      const fields = tiers.get(object);
      if (!fields.has(key)) fields.set(key, {original: object[key], applied: object[key]});
      const record = fields.get(key);
      if (object[key] !== record.applied) record.original = object[key];
      const baseline = state.country === 'AU' ? 99 : 79;
      const unit = activeField.unit || (record.original === baseline ? 1 : record.original === baseline * 100 ? 100 : null);
      let written = !!unit && record.original === baseline * unit;
      try {
        if (written && object[key] !== state.threshold * unit) object[key] = state.threshold * unit;
        written = written && object[key] === state.threshold * unit;
      } catch (_) { written = false; }
      record.applied = object[key];
      // Verify Rebuy's own currency resolver, rather than merely accepting a write.
      if (written && typeof smartCart.getTierMinimum === 'function') {
        try { written = smartCart.getTierMinimum(activeTier) === state.threshold * 100; }
        catch (_) { written = false; }
      }
      rebuyStatus = written ? 'updated' : 'unsupported-schema';
    }
    // A diagnostic for QA, never a substitute for checking checkout entitlement.
    const blocked = state?.isPreview && rebuyStatus !== 'updated';
    let notice = document.getElementById('moodi-shipping-preview-warning');
    if (blocked && !notice) {
      notice = document.createElement('aside');
      notice.id = 'moodi-shipping-preview-warning';
      notice.setAttribute('role', 'status');
      // Normal flow keeps the diagnostic clear of sticky add-to-cart/cart controls.
      notice.style.cssText = 'position:relative;box-sizing:border-box;margin:12px;padding:12px;background:#fff0cc;color:#222;border:2px solid #9b6600;font:14px/1.4 sans-serif;overflow-wrap:anywhere';
      document.body.insertBefore(notice, document.body.firstChild);
    }
    if (notice) {
      notice.hidden = !blocked;
      const message = 'Shipping preview QA blocked: Rebuy integration is ' + rebuyStatus +
        '. Verify the installed currency model and cart display before launch.';
      if (blocked && notice.textContent !== message) notice.textContent = message;
    }
    root.dispatchEvent(new CustomEvent('moodi:shipping-state', {detail: {state, rebuyStatus}}));
  }
  function schedule() {
    if (!queued) { queued = true; root.requestAnimationFrame(refresh); }
  }
  // ig:ready is documented. Also handle a late SDK that misses that event.
  // Bound retries to one minute; events/pageshow continue working afterwards.
  let sdkRetry = null;
  function awaitSdk(attempt = 0) {
    if (typeof root.igData?.user?.getExperiments === 'function' &&
        typeof root.igData?.user?.getTestGroup === 'function') { schedule(); return; }
    if (attempt < 120) sdkRetry = root.setTimeout(() => awaitSdk(attempt + 1), 500);
  }
  awaitSdk();
  root.addEventListener('pagehide', () => root.clearTimeout(sdkRetry));
  root.addEventListener('ig:ready', schedule);
  root.addEventListener('pageshow', () => {
    root.clearTimeout(sdkRetry);
    awaitSdk();
    schedule();
  });
  root.addEventListener('moodi:shipping-refresh', schedule);
  document.addEventListener('rebuy:smartcart.ready', schedule);
  document.addEventListener('rebuy:cart.change', schedule);
  document.addEventListener('shopify:section:load', schedule);
  // Watch new text nodes only, not our own text updates (no observer loop).
  new MutationObserver(records => {
    if (records.some(record => record.addedNodes.length || record.removedNodes.length)) schedule();
  }).observe(document.body, {childList: true, subtree: true});
  schedule();
})(typeof window === 'undefined' ? globalThis : window);
